import React from "react";

// Categorical palettes drawn from the module theme colours (handoff §7).
export const SEGMENT_COLORS = {
  "2W": "var(--mt-theme-cyan)",
  "3W": "var(--mt-theme-orange)",
  Car: "var(--mt-theme-blue)",
  CV: "var(--mt-theme-purple)",
  Tractor: "var(--mt-theme-green)",
};
export const SOURCE_COLORS = {
  Website: "#2196f3",
  "Field Visit": "#4caf50",
  Referral: "#ff9800",
  "Call Center": "#9c27b0",
  WhatsApp: "#00acc1",
  Auction: "#f44336",
};

// ---- Section card (white stat-card surface) --------------------------------
export function Panel({ title, hint, action, children, style }) {
  return (
    <section
      className="mt-card"
      style={{
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        border: "1px solid var(--mt-border)",
        ...style,
      }}
    >
      {(title || action) && (
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <h3
            style={{
              margin: 0,
              fontSize: "var(--mt-text-base)",
              fontWeight: "var(--mt-fw-bold)",
              color: "var(--mt-ink)",
              letterSpacing: "var(--mt-tracking-tight)",
            }}
          >
            {title}
          </h3>
          {hint && (
            <span style={{ fontSize: "var(--mt-text-xs)", color: "var(--mt-muted)" }}>
              {hint}
            </span>
          )}
          {action && <div style={{ marginLeft: "auto" }}>{action}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

// ---- Horizontal bar list ---------------------------------------------------
export function HBars({ items, color, colorMap }) {
  const list = items || [];
  const max = Math.max(...list.map((i) => i.value), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
      {list.map((it) => {
        const c = (colorMap && colorMap[it.label]) || color || "var(--mt-theme-red)";
        return (
          <div
            key={it.label}
            style={{
              display: "grid",
              gridTemplateColumns: "84px 1fr 30px",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span
              style={{
                fontSize: "var(--mt-text-sm)",
                color: "var(--mt-ink-soft)",
                fontWeight: 600,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {it.label}
            </span>
            <div
              style={{
                height: 18,
                background: "var(--mt-gray-100)",
                borderRadius: 5,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: (it.value / max) * 100 + "%",
                  height: "100%",
                  background: c,
                  borderRadius: 5,
                  transition: "width var(--mt-dur-normal) var(--mt-ease-out)",
                  minWidth: it.value ? 4 : 0,
                }}
              />
            </div>
            <span
              style={{
                fontSize: "var(--mt-text-sm)",
                color: "var(--mt-ink)",
                fontWeight: 700,
                textAlign: "right",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {it.value}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ---- Funnel (label + value above a proportional track — never clips) -------
export function Funnel({ stages }) {
  const list = stages || [];
  const max = Math.max(...list.map((s) => s.value), 1);
  const reds = ["#ff9da1", "#ff6b70", "#f44336", "#b8101a"];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {list.map((st, i) => {
        const w = Math.max((st.value / max) * 100, 3);
        const conv =
          i > 0 && list[i - 1].value
            ? Math.round((st.value / list[i - 1].value) * 100)
            : null;
        return (
          <div key={st.label}>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                gap: 8,
                marginBottom: 5,
              }}
            >
              <span
                style={{
                  fontSize: "var(--mt-text-sm)",
                  fontWeight: 600,
                  color: "var(--mt-ink-soft)",
                  whiteSpace: "nowrap",
                }}
              >
                {st.label}
                {conv !== null && (
                  <span style={{ color: "var(--mt-muted)", fontWeight: 500, marginLeft: 6 }}>
                    {conv}%
                  </span>
                )}
              </span>
              <span
                style={{
                  fontSize: "var(--mt-text-base)",
                  fontWeight: 800,
                  color: "var(--mt-ink)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {st.value}
              </span>
            </div>
            <div
              style={{
                height: 20,
                background: "var(--mt-gray-100)",
                borderRadius: 6,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: w + "%",
                  height: "100%",
                  background: reds[i % reds.length],
                  borderRadius: 6,
                  minWidth: st.value ? 4 : 0,
                  transition: "width var(--mt-dur-normal) var(--mt-ease-out)",
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---- Donut (conic-gradient) + legend ---------------------------------------
export function Donut({ items, colorMap }) {
  const list = items || [];
  const total = list.reduce((s, i) => s + i.value, 0) || 1;
  let acc = 0;
  const stops = list
    .map((it) => {
      const start = (acc / total) * 360;
      acc += it.value;
      const end = (acc / total) * 360;
      const c = (colorMap && colorMap[it.label]) || "var(--mt-theme-red)";
      return `${c} ${start}deg ${end}deg`;
    })
    .join(", ");
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
      <div style={{ position: "relative", width: 116, height: 116, flex: "none" }}>
        <div
          style={{
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            background: list.length ? `conic-gradient(${stops})` : "var(--mt-gray-100)",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 26,
            borderRadius: "50%",
            background: "var(--mt-surface)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "inset 0 1px 3px rgba(0,0,0,0.06)",
          }}
        >
          <span
            style={{
              fontSize: "var(--mt-text-lg)",
              fontWeight: 800,
              color: "var(--mt-ink)",
              lineHeight: 1,
            }}
          >
            {list.reduce((s, i) => s + i.value, 0)}
          </span>
          <span
            style={{
              fontSize: 10,
              color: "var(--mt-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              marginTop: 2,
            }}
          >
            leads
          </span>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 7, flex: 1 }}>
        {list.map((it) => (
          <div key={it.label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                width: 9,
                height: 9,
                borderRadius: 2,
                background: (colorMap && colorMap[it.label]) || "var(--mt-theme-red)",
                flex: "none",
              }}
            />
            <span
              style={{
                fontSize: "var(--mt-text-sm)",
                color: "var(--mt-ink-soft)",
                flex: 1,
                whiteSpace: "nowrap",
              }}
            >
              {it.label}
            </span>
            <span
              style={{
                fontSize: "var(--mt-text-sm)",
                fontWeight: 700,
                color: "var(--mt-ink)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {it.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Vertical bars (activity trend) ----------------------------------------
export function Bars({ data, color = "var(--mt-theme-red)", height = 92 }) {
  const list = data || [];
  const max = Math.max(...list.map((d) => d.count), 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height }}>
      {list.map((d, i) => {
        const last = i === list.length - 1;
        const h = Math.max((d.count / max) * 100, 3);
        return (
          <div
            key={d.date || i}
            title={`${d.count}`}
            style={{ flex: 1, height: "100%", display: "flex", alignItems: "flex-end" }}
          >
            <div
              style={{
                width: "100%",
                height: h + "%",
                minHeight: 2,
                borderRadius: "3px 3px 0 0",
                background: last ? color : "var(--mt-gray-200)",
                transition: "height var(--mt-dur-normal) var(--mt-ease-out)",
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

// ---- Sparkline (area line, emphasized endpoint) ----------------------------
export function Sparkline({ data, color = "var(--mt-theme-green)", height = 92 }) {
  const list = data || [];
  const n = list.length;
  if (!n) return <div style={{ height }} />;
  const w = 300;
  const pad = 6;
  const max = Math.max(...list.map((d) => d.count), 1);
  const pts = list.map((d, i) => {
    const x = pad + (i * (w - 2 * pad)) / Math.max(n - 1, 1);
    const y = height - pad - (d.count / max) * (height - 2 * pad);
    return [x, y];
  });
  const line = pts
    .map((p, i) => (i ? "L" : "M") + p[0].toFixed(1) + " " + p[1].toFixed(1))
    .join(" ");
  const area = `${line} L${pts[n - 1][0].toFixed(1)} ${height - pad} L${pts[0][0].toFixed(
    1,
  )} ${height - pad} Z`;
  const gid = "spk" + Math.random().toString(36).slice(2, 8);
  return (
    <svg
      viewBox={`0 0 ${w} ${height}`}
      preserveAspectRatio="none"
      style={{ width: "100%", height, display: "block" }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gid} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor={color} stopOpacity="0.28" />
          <stop offset="1" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={pts[n - 1][0].toFixed(1)} cy={pts[n - 1][1].toFixed(1)} r="3.5" fill={color} />
    </svg>
  );
}

// ---- Rate bars (volume + a conversion-rate suffix) -------------------------
export function RateBars({ items, color = "var(--mt-theme-red)" }) {
  const list = items || [];
  const max = Math.max(...list.map((i) => i.value), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
      {list.map((it) => (
        <div
          key={it.label}
          style={{ display: "grid", gridTemplateColumns: "96px 1fr auto", alignItems: "center", gap: 10 }}
        >
          <span
            style={{
              fontSize: "var(--mt-text-sm)",
              color: "var(--mt-ink-soft)",
              fontWeight: 600,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {it.label}
          </span>
          <div style={{ height: 18, background: "var(--mt-gray-100)", borderRadius: 5, overflow: "hidden" }}>
            <div
              style={{
                width: (it.value / max) * 100 + "%",
                height: "100%",
                background: color,
                borderRadius: 5,
                minWidth: it.value ? 4 : 0,
              }}
            />
          </div>
          <span
            style={{
              fontSize: "var(--mt-text-sm)",
              fontWeight: 700,
              textAlign: "right",
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
            }}
          >
            {it.value}
            {it.rate != null && (
              <span style={{ color: "var(--mt-theme-green)", fontSize: "var(--mt-text-xs)", marginLeft: 5 }}>
                {it.rate}%
              </span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

// ---- Target ring (agent personal achievement) ------------------------------
export function TargetRing({ value, target, label }) {
  const p = Math.min(Math.round((value / (target || 1)) * 100), 100);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
      <div style={{ position: "relative", width: 110, height: 110, flex: "none" }}>
        <div
          style={{
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            background: `conic-gradient(var(--mt-theme-red) ${p * 3.6}deg, var(--mt-gray-200) 0deg)`,
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 12,
            borderRadius: "50%",
            background: "var(--mt-surface)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span
            style={{
              fontSize: "var(--mt-text-xl)",
              fontWeight: 800,
              color: "var(--mt-ink)",
              lineHeight: 1,
            }}
          >
            {p}%
          </span>
          <span style={{ fontSize: 10, color: "var(--mt-muted)", marginTop: 2 }}>
            of target
          </span>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span
          style={{
            fontSize: "var(--mt-text-2xl)",
            fontWeight: 800,
            color: "var(--mt-ink)",
            lineHeight: 1,
          }}
        >
          {value}
        </span>
        <span style={{ fontSize: "var(--mt-text-sm)", color: "var(--mt-muted)" }}>
          {label} · target {target}
        </span>
      </div>
    </div>
  );
}
